import React from 'react';
import { AlertTriangle, CheckCircle, XCircle, AlertCircle } from 'lucide-react';

/**
 * ApprovalFlags Component
 * Displays approval requirements based on business thresholds
 * 
 * Thresholds:
 * - Annual Gross Revenue < $10K: Director of Sales approval
 * - Annual Gross Revenue < $7K: CFO/CEO approval
 * - Net Profit Margin < 55%: Director of Sales approval
 * - Net Profit Margin < 48%: CFO/CEO approval
 * - Annual Net Revenue > $50K: CEO/CFO approval
 * - Minimum CC Fee below proposed rate: CEO/CFO approval
 */
export default function ApprovalFlags({ profitabilityData, scenarioInfo }) {
  if (!profitabilityData || !profitabilityData.success) {
    return null;
  }

  const { totals, net_fees } = profitabilityData;
  
  // Calculate key metrics
  const annualGrossRevenue = totals?.total_fees || 0;  // In actual dollars
  const annualNetRevenue = totals?.net_revenue || 0;   // After merchant fees
  const gpm = totals?.gpm || 0;  // Net Profit Margin (GPM)
  
  // Determine approvals needed
  const approvals = [];
  
  // Revenue Thresholds
  if (annualGrossRevenue < 7000) {
    approvals.push({
      level: 'critical',
      category: 'Revenue',
      message: `Annual Gross Revenue ($${annualGrossRevenue.toLocaleString()}) < $7K`,
      approver: 'CFO / CEO',
      icon: XCircle,
      color: 'red'
    });
  } else if (annualGrossRevenue < 10000) {
    approvals.push({
      level: 'warning',
      category: 'Revenue',
      message: `Annual Gross Revenue ($${annualGrossRevenue.toLocaleString()}) < $10K`,
      approver: 'Director of Sales',
      icon: AlertTriangle,
      color: 'yellow'
    });
  }
  
  // Net Revenue Threshold
  if (annualNetRevenue > 50000) {
    approvals.push({
      level: 'info',
      category: 'High Value',
      message: `Annual Net Revenue ($${annualNetRevenue.toLocaleString()}) > $50K`,
      approver: 'CEO / CFO',
      icon: AlertCircle,
      color: 'blue'
    });
  }
  
  // Net Profit Margin Thresholds
  if (gpm < 48) {
    approvals.push({
      level: 'critical',
      category: 'Margin',
      message: `Net Profit Margin (${gpm.toFixed(1)}%) < 48% minimum`,
      approver: 'CFO / CEO',
      icon: XCircle,
      color: 'red'
    });
  } else if (gpm < 55) {
    approvals.push({
      level: 'warning',
      category: 'Margin',
      message: `Net Profit Margin (${gpm.toFixed(1)}%) < 55% target`,
      approver: 'Director of Sales',
      icon: AlertTriangle,
      color: 'yellow'
    });
  }
  
  // CC Fee Minimum Check (if we have proposed pricing data)
  // This would need to check if any CC fees are below the minimum thresholds
  
  // If no approvals needed
  if (approvals.length === 0) {
    return (
      <div className="card p-4 bg-green-50 border-green-200">
        <div className="flex items-center gap-2 text-green-700">
          <CheckCircle className="w-5 h-5" />
          <span className="font-semibold">No Special Approvals Required</span>
        </div>
        <p className="text-sm text-green-600 mt-1">
          This pricing meets all standard thresholds and can proceed through normal approval workflow.
        </p>
      </div>
    );
  }
  
  return (
    <div className="card p-4 space-y-3">
      <h3 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
        <AlertTriangle className="w-5 h-5 text-amber-500" />
        Approval Requirements
      </h3>
      
      <div className="space-y-2">
        {approvals.map((approval, index) => {
          const Icon = approval.icon;
          const colorClasses = {
            red: 'bg-red-50 border-red-200 text-red-800',
            yellow: 'bg-amber-50 border-amber-200 text-amber-800',
            blue: 'bg-blue-50 border-blue-200 text-blue-800'
          };
          
          const iconColors = {
            red: 'text-red-600',
            yellow: 'text-amber-600',
            blue: 'text-blue-600'
          };
          
          return (
            <div 
              key={index}
              className={`p-3 rounded-lg border ${colorClasses[approval.color]}`}
            >
              <div className="flex items-start gap-3">
                <Icon className={`w-5 h-5 mt-0.5 ${iconColors[approval.color]}`} />
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-medium text-sm">{approval.category}</span>
                    <span className="text-xs font-semibold px-2 py-1 bg-white rounded">
                      Requires: {approval.approver}
                    </span>
                  </div>
                  <p className="text-sm">{approval.message}</p>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      
      <div className="mt-4 p-3 bg-slate-50 rounded-lg border border-slate-200">
        <p className="text-xs text-slate-600">
          <strong>Note:</strong> All approvals must be obtained before proceeding with this pricing proposal.
          Contact the Pricing Team (Kevin Nolan) for guidance on the approval workflow.
        </p>
      </div>
    </div>
  );
}
